









import { Component, Vue, Prop, Ref } from "vue-property-decorator";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { getActiveUser } from "@/data/repository/UserRepository";
import { deleteProjectAccess, restoreProjectAccess } from "@/permissions/UserPermissions";
import ProjectOverview, { isProjectDeleted } from "@/data/models/ProjectOverview";
import { deleteProject, restoreProject, deleteProjectPermanently } from "@/data/repository/ProjectRepository";
import {
  RestoreAndDeleteAction,
  resolveRestoreAndDeleteButtons,
  resolveRestoreAndDeleteTitleKey,
} from "@/utils/RestoreAndDeleteProjectAction";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class RestoreAndDeleteProjectsDialog extends Vue {
  @Prop() showSnackbar!: (
    isSuccessSnackbar: boolean,
    message: string,
    parameter: string
  ) => void;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  /**
   * The project the dialog acts on. One dialog serves the whole list, so
   * `show()` is handed the project instead of the dialog reading it from a
   * prop: a prop only reaches the dialog on the render after the click, which
   * would make opening the dialog depend on a render having happened.
   */
  projectOverview: ProjectOverview | null = null;

  /** Whether that project sits in the archive (archived or deleted). */
  isArchivedProject = false;

  closeButtonText = this.$t("common.back").toString();
  restoreButtonText = this.$t("common.restore").toString();

  get isDeletedProject() {
    const projectOverview = this.projectOverview;

    return !!projectOverview && isProjectDeleted(projectOverview);
  }

  get deleteButtonText() {
    return this.isDeletedProject
      ? this.$t("archivedProjectList.permanentlyDelete").toString()
      : this.$t("common.delete").toString();
  }

  get activeUser() {
    return getActiveUser();
  }

  get canRestoreProject() {
    return restoreProjectAccess(this.activeUser) &&
      (
        this.isArchivedProject ||
        this.isDeletedProject
      );
  }

  get canDeleteProject() {
    return deleteProjectAccess(this.activeUser);
  }

  async handleNeutralPositiveAction() {
    if (this.canDeleteProject) {
      await this.onDeleteProject();
    }
  }

  async handlePositiveAction() {
    if (this.canRestoreProject) {
      await this.onRestoreProject();
    } else if (this.canDeleteProject) {
      await this.onDeleteProject();
    }
  }

  async onRestoreProject() {
    const projectOverview = this.projectOverview;

    if (projectOverview && this.canRestoreProject) {
      this.$emit("isProcessingProject");

      const result = await restoreProject(projectOverview.id);

      if (result) {
        this.showSnackbar(
          true, 
          "archivedProjects.restorationConfirmationDialog.successMessage", 
          projectOverview.projectName
        );
      }
      else {
        this.showSnackbar( 
          false, 
          "archivedProjects.restorationConfirmationDialog.errorMessage", 
          projectOverview.projectName
        );
      }
    }

    return;
  }

  async onDeleteProject() {
    const projectOverview = this.projectOverview;

    if (projectOverview && this.canDeleteProject) {
      this.$emit("isProcessingProject");

      const result = this.isDeletedProject
        ? await deleteProjectPermanently(projectOverview.id)
        : await deleteProject(projectOverview.id, this.isArchivedProject);

      if (result) {
        this.showSnackbar(
          true,
          `archivedProjects.deletionConfirmationDialog.${this.isDeletedProject
            ? "successMessageAlt"
            : "successMessage"
          }`,
          projectOverview.projectName
        );
      }
      else {
        this.showSnackbar(
          false,
          `archivedProjects.deletionConfirmationDialog.${this.isDeletedProject
            ? "errorMessageAlt"
            : "errorMessage"
          }`,
          projectOverview.projectName
        );
      }
    }

    return;
  }

  buttonText(action: RestoreAndDeleteAction) {
    switch (action) {
      case RestoreAndDeleteAction.RESTORE:
        return this.restoreButtonText;
      case RestoreAndDeleteAction.DELETE:
        return this.deleteButtonText;
      default:
        return "";
    }
  }

  /**
   * Opens the dialog for one project. Everything it displays is derived from
   * the arguments and from the current user's access, so a click opens it
   * without waiting for a render.
   */
  show(projectOverview: ProjectOverview, isArchivedProject: boolean) {
    this.projectOverview = projectOverview;
    this.isArchivedProject = isArchivedProject;

    const access = {
      canRestoreProject: this.canRestoreProject,
      canDeleteProject: this.canDeleteProject,
    };

    const buttons = resolveRestoreAndDeleteButtons(access);
    const positiveButtonText = this.buttonText(buttons.positive);

    const title = this.$t(
      resolveRestoreAndDeleteTitleKey({
        ...access,
        isArchivedProject: this.isArchivedProject,
        isDeletedProject: this.isDeletedProject,
      }),
      [projectOverview.projectName]
    ).toString();

    this.baseDialogComponent.show({
      title: title,
      positiveButtonText: positiveButtonText,
      negativeButtonText: this.closeButtonText,
      neutralPositiveButtonText: this.buttonText(buttons.neutralPositive),
      closeOnSave: true,
      closeOnNeutralAction: true,
      maxWidth: 600,
      hidePositiveButtonIfEmpty: true,
      hideSaveButton: positiveButtonText === ""
    });
  }
}
